import _ from 'lodash';
import { createSelector } from 'reselect';
import { getSelectedProject } from '../../redux/projects/projects.selectors';
import { StoreState } from '../../redux/reducers/store.types';
import { Project } from '../../types/entities';
import { navigation } from '../../utils';
import { roles } from '../roles/role.constants';
import {
  getCurrentUserRole,
  getCurrentUserRoleWithoutProject,
} from '../roles/role.selectors';
import { Role } from '../roles/role.types';

import { SidebarMenuItem, SidebarState } from './sidebar.types';

const sidebar = (state: StoreState) => state.sidebar;

const organizationLevelMenuItems = createSelector(
  sidebar,
  (sidebar: SidebarState): SidebarMenuItem[] => sidebar.organizationLevel,
);

const projectLevelMenuItems = createSelector(
  sidebar,
  (sidebar: SidebarState) => sidebar.projectLevel,
);

export const getOrganizationLevelMenuItems = createSelector(
  organizationLevelMenuItems,
  getCurrentUserRoleWithoutProject,
  (
    items: SidebarMenuItem[],
    currentUserRole: Role | undefined,
  ): SidebarMenuItem[] => {
    return _.filter<SidebarMenuItem>(
      items,
      (item: SidebarMenuItem): boolean => {
        if (roles.sidebar.organizationLevel[item.id]) {
          return item.isAllowed(
            roles.sidebar.organizationLevel[item.id][currentUserRole],
          );
        }

        return item.isAllowed(false);
      },
    );
  },
);

export const getProjectLevelMenuItems = createSelector(
  projectLevelMenuItems,
  getCurrentUserRole,
  getSelectedProject,
  (
    items: SidebarMenuItem[],
    currentUserRole: Role | undefined,
    project: Project,
  ): SidebarMenuItem[] => {
    return _.filter<SidebarMenuItem>(
      items,
      (item: SidebarMenuItem): boolean => {
        if (roles.sidebar.projectLevel[item.id]) {
          return item.isAllowed(
            roles.sidebar.projectLevel[item.id][currentUserRole],
            project,
          );
        }

        return item.isAllowed(false, project);
      },
    );
  },
);

const getSelectedMenuItem = createSelector(
  // This needed for detecting active menu
  (state) => state,
  sidebar,
  (
    state: StoreState,
    { organizationLevel, projectLevel }: SidebarState,
  ): SidebarMenuItem | undefined => {
    return _.find<SidebarMenuItem>(
      [...organizationLevel, ...projectLevel],
      (item: SidebarMenuItem) => {
        return _([item.path])
          .concat(item.subPaths || [])
          .compact()
          .some((path: string): boolean => navigation.isActivePath(path));
      },
    );
  },
);

export const getSelectedMenuItemId = createSelector(
  getSelectedMenuItem,
  (item: SidebarMenuItem): string => item && item.id,
);
